import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { FormikErrors, useFormik } from 'formik';
import { AnyObjectSchema } from 'yup';

import { OfferDesignFormValues, UniqueFlag } from 'views/OfferDesign/OfferDesign.types';

interface IFieldProps {
  name: string;
  value: unknown;
  onChange: (e: React.ChangeEvent<unknown>) => void;
  onBlur: (e: React.FocusEvent<unknown>) => void;
}

interface IUniqueFlags {
  isNameUnique: boolean;
  isExternalIdUnique: boolean;
}

interface IFormFieldsContext {
  values: OfferDesignFormValues;
  getFieldProps: (name: string) => IFieldProps;
  setFieldValue: (field: string, value: unknown) => void;
  getFieldError: (name: string, uniqueFlag?: UniqueFlag) => string | undefined;
  isFieldTouched: (name: string) => boolean;
  uniqueFlags: IUniqueFlags;
}

const FormFieldsContext = createContext<IFormFieldsContext | null>(null);

/**
 * Provides form state to all FormFields sub-components via a library-agnostic API.
 *
 * @example
 * const { values, getFieldProps, setFieldValue } = useFormFieldsContext();
 */
export const useFormFieldsContext = (): IFormFieldsContext => {
  const ctx = useContext(FormFieldsContext);
  if (!ctx) {
    throw new Error('useFormFieldsContext must be used within a FormFieldsProvider');
  }
  return ctx;
};

interface IUseFormFieldsConfig {
  initialValues: OfferDesignFormValues;
  validationSchema?: AnyObjectSchema;
  onSubmit: (formValues: OfferDesignFormValues) => Promise<void> | void;
  uniqueFlags: IUniqueFlags;
}

interface IUseFormFieldsReturn {
  contextValue: IFormFieldsContext;
  submitForm: () => Promise<void>;
  validateForm: () => Promise<FormikErrors<OfferDesignFormValues>>;
  setTouched: (touched: Record<string, boolean>, shouldValidate?: boolean) => void;
  isValid: boolean;
  dirty: boolean;
  values: OfferDesignFormValues;
}

/**
 * Initializes the form and builds the context value.
 * Internally uses Formik — swap the implementation here to change form libraries.
 *
 * @example
 * const { contextValue, submitForm, isValid, dirty, values } = useFormFields({
 *   initialValues,
 *   validationSchema: schema,
 *   onSubmit: handleSubmit,
 *   uniqueFlags: { isNameUnique: true, isExternalIdUnique: true }
 * });
 */
export const useFormFields = ({
  initialValues,
  validationSchema,
  onSubmit,
  uniqueFlags
}: IUseFormFieldsConfig): IUseFormFieldsReturn => {
  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit
  });

  const getError = (name: string): string | undefined => {
    const error = formik.errors[name as keyof OfferDesignFormValues];
    return typeof error === 'string' ? error : undefined;
  };

  const isTouched = (name: string): boolean =>
    !!formik.touched[name as keyof OfferDesignFormValues];

  const getFieldProps = (name: string): IFieldProps => ({
    name,
    value: formik.values[name as keyof OfferDesignFormValues],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur
  });

  const getFieldError = useCallback((name: string, uniqueFlag?: UniqueFlag): string | undefined => {
    const validationError = isTouched(name) ? getError(name) : undefined;
    const isUniquenessViolation = uniqueFlag && !uniqueFlags[uniqueFlag];
    const fieldLabel = name === 'name' ? 'Name' : 'External ID';
    const uniqueError = isUniquenessViolation ? `${fieldLabel} already exists` : '';
    return validationError || uniqueError || undefined;
  }, [formik.errors, formik.touched, uniqueFlags]);

  const isFieldTouched = useCallback((name: string): boolean => isTouched(name), [formik.touched]);

  const contextValue = useMemo((): IFormFieldsContext => ({
    values: formik.values,
    getFieldProps,
    setFieldValue: formik.setFieldValue,
    getFieldError,
    isFieldTouched,
    uniqueFlags
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [formik.values, formik.setFieldValue, getFieldError, isFieldTouched, uniqueFlags]);

  const validateForm = async (): Promise<FormikErrors<OfferDesignFormValues>> =>
    formik.validateForm();

  const setTouched = (fields: Record<string, boolean>, shouldValidate?: boolean): void => {
    formik.setTouched(fields, shouldValidate);
  };

  return {
    contextValue,
    submitForm: formik.submitForm,
    validateForm,
    setTouched,
    isValid: formik.isValid,
    dirty: formik.dirty,
    values: formik.values
  };
};

export { FormFieldsContext };
